/**
 * Visit lifecycle: visit_start, route_view, visit_end.
 *
 * The job-view counters only ever saw two pages — the home feed and a job page — and the
 * home one only after the browser went idle (up to 3 s). Someone landing on /location,
 * /search, /company/:id… or closing the tab within those seconds was never counted, and
 * those are exactly the visits a "why do people leave" question is about. This records
 * every visit from its first moment, the path through the app, and how it ended.
 *
 * - visit_start  once per session, built the moment the app loads (occurredAt is load
 *                time) and sent when the browser is idle — or on the way out, if the
 *                visitor leaves before that.
 * - route_view   every in-app navigation after the landing page, carrying how long the
 *                previous page was open and how far it was scrolled.
 * - visit_end    whenever the page is hidden or closed. A visitor who switches apps and
 *                comes back produces more than one; the latest per session is the exit.
 */

import { trackEvent, EVENTS, countJobsViewedThisSession, firstTimeThisSession } from './tracking';
import { detectInAppBrowser } from './sourceDetection';
import { getViewSessionId, registerVisit, sessionGetJson, sessionSetJson } from './trackingSession';

const CLOCK_KEY = 'diil_visit_clock';

const state = {
  initialized: false,
  pendingStart: null, // visit_start context waiting for idle time
  currentPath: null,
  pageEnteredAt: 0,
  pageMaxScrollPct: 0,
  visibleSince: null,
  endSent: false,
  timingsSent: false,
  lcpMs: null,
};

// --- Session clock (survives reloads within the session) --------------------------------

const readClock = () => {
  const sessionId = getViewSessionId();
  const stored = sessionGetJson(CLOCK_KEY, null);
  if (stored && stored.sid === sessionId) return stored;
  return { sid: sessionId, startedAt: Date.now(), activeMs: 0, pages: 0 };
};

const writeClock = (clock) => sessionSetJson(CLOCK_KEY, clock);

const now = () => Date.now();

const settleActiveTime = () => {
  if (state.visibleSince == null) return;
  const clock = readClock();
  clock.activeMs += Math.max(0, now() - state.visibleSince);
  writeClock(clock);
  state.visibleSince = null;
};

// --- Environment -----------------------------------------------------------------------------

const referrerHost = () => {
  try {
    if (!document.referrer) return null;
    return new URL(document.referrer).hostname.toLowerCase() || null;
  } catch (error) {
    return null;
  }
};

const deviceType = (ua) => {
  if (/iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i.test(ua)) return 'tablet';
  if (/Mobi|iPhone|iPod|Android/i.test(ua)) return 'mobile';
  return 'desktop';
};

const navigationType = () => {
  try {
    return performance.getEntriesByType('navigation')[0]?.type || null;
  } catch (error) {
    return null;
  }
};

const pageTimings = () => {
  const out = {};
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav) {
      if (nav.responseStart > 0) out.ttfbMs = Math.round(nav.responseStart);
      if (nav.domContentLoadedEventEnd > 0) out.dclMs = Math.round(nav.domContentLoadedEventEnd);
      // 0 until the load event has finished — a visit that ends without loadMs left
      // before the page finished loading.
      if (nav.loadEventEnd > 0) out.loadMs = Math.round(nav.loadEventEnd);
    }
    const fcp = performance.getEntriesByName('first-contentful-paint')[0];
    if (fcp) out.fcpMs = Math.round(fcp.startTime);
  } catch (error) {
    // Navigation Timing unavailable
  }
  if (state.lcpMs != null) out.lcpMs = state.lcpMs;
  return out;
};

// --- Scroll depth ------------------------------------------------------------------------------
//
// Measured on whatever is actually scrolling — on phones the feed scrolls inside its own
// container, not the window — counting only scrollers at least half the screen tall, so a
// dropdown list hitting its bottom does not read as reading the whole page.
// 0 = never scrolled; otherwise the share of the page height that has been on screen.

let scrollTarget = null;
let scrollScheduled = false;

const measureScroll = () => {
  scrollScheduled = false;
  try {
    const target = scrollTarget;
    let top;
    let height;
    let client;
    if (!target || target === document || target === document.documentElement || target === document.body) {
      const el = document.scrollingElement || document.documentElement;
      top = el.scrollTop;
      height = el.scrollHeight;
      client = window.innerHeight;
    } else if (target.nodeType === 1) {
      if (target.clientHeight < window.innerHeight * 0.5) return;
      top = target.scrollTop;
      height = target.scrollHeight;
      client = target.clientHeight;
    } else {
      return;
    }
    if (!height || height <= client + 10 || top <= 0) return;
    const pct = Math.min(100, Math.round(((top + client) / height) * 100));
    if (pct > state.pageMaxScrollPct) state.pageMaxScrollPct = pct;
  } catch (error) {
    // ignore
  }
};

const onScroll = (event) => {
  scrollTarget = event.target;
  if (scrollScheduled) return;
  scrollScheduled = true;
  (window.requestAnimationFrame || ((fn) => setTimeout(fn, 16)))(measureScroll);
};

// --- visit_start -----------------------------------------------------------------------------

const flushVisitStart = (unload) => {
  const context = state.pendingStart;
  if (!context) return;
  state.pendingStart = null;
  trackEvent(EVENTS.VISIT_START, context, { unload });
};

const prepareVisitStart = () => {
  if (!firstTimeThisSession('visit_start')) return;
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const { visitNumber, daysSinceLastVisit } = registerVisit();
  let landingPath = null;
  try {
    landingPath = window.location.pathname;
  } catch (error) {
    // ignore
  }
  state.pendingStart = {
    occurredAt: new Date().toISOString(),
    meta: {
      path: landingPath,
      landingPath,
      referrerHost: referrerHost(),
      inApp: detectInAppBrowser(ua),
      device: deviceType(ua),
      viewportW: typeof window !== 'undefined' ? window.innerWidth : null,
      lang: typeof navigator !== 'undefined' ? navigator.language : null,
      navType: navigationType(),
      visitNumber,
      daysSinceLastVisit,
    },
  };
  // Nothing on screen waits for this, so it does not compete with the first load. A
  // visitor who leaves before the browser is idle is sent from the pagehide handler.
  const send = () => flushVisitStart(false);
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(send, { timeout: 2000 });
  } else {
    setTimeout(send, 1500);
  }
};

// --- visit_end -------------------------------------------------------------------------------

const sendVisitEnd = (reason) => {
  if (state.endSent) return;
  state.endSent = true;
  flushVisitStart(true);
  settleActiveTime();
  const clock = readClock();
  const meta = {
    path: state.currentPath,
    reason,
    durationSec: Math.round((now() - clock.startedAt) / 1000),
    activeSec: Math.round(clock.activeMs / 1000),
    pages: clock.pages,
    lastPath: state.currentPath,
    lastSec: state.pageEnteredAt ? Math.round((now() - state.pageEnteredAt) / 1000) : null,
    lastScrollPct: state.pageMaxScrollPct,
    jobsViewed: countJobsViewedThisSession(),
  };
  // Load timings once per page load — the first exit is the one that says whether the
  // page had even finished loading.
  if (!state.timingsSent) {
    state.timingsSent = true;
    Object.assign(meta, pageTimings());
  }
  trackEvent(EVENTS.VISIT_END, { meta }, { unload: true });
};

const onVisibilityChange = () => {
  try {
    if (document.visibilityState === 'hidden') {
      sendVisitEnd('hidden');
    } else {
      state.endSent = false;
      state.visibleSince = now();
    }
  } catch (error) {
    // ignore
  }
};

// --- Public API --------------------------------------------------------------------------------

/**
 * Start visit tracking. Call once, at app start, after initSourceTracking().
 */
export function initVisitTracking() {
  if (state.initialized || typeof window === 'undefined') return;
  state.initialized = true;
  try {
    // Make sure the session clock exists from the first moment of the session
    writeClock(readClock());
    state.visibleSince = document.visibilityState === 'hidden' ? null : now();
    prepareVisitStart();

    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', () => sendVisitEnd('pagehide'));

    if (typeof PerformanceObserver === 'function') {
      try {
        new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const last = entries[entries.length - 1];
          if (last) state.lcpMs = Math.round(last.startTime);
        }).observe({ type: 'largest-contentful-paint', buffered: true });
      } catch (error) {
        // LCP not supported (Safari < 17, Firefox)
      }
    }
  } catch (error) {
    // tracking must never stop the app from starting
  }
}

/**
 * Record an in-app navigation. Called by TrackingRoot on every pathname change; the
 * first call is the landing page (already covered by visit_start) and is not sent.
 */
export function onRouteChange(pathname) {
  try {
    if (pathname === state.currentPath) return;
    const clock = readClock();
    clock.pages += 1;
    writeClock(clock);

    const previousPath = state.currentPath;
    const previousSec = state.pageEnteredAt ? Math.round((now() - state.pageEnteredAt) / 1000) : null;
    const previousScroll = state.pageMaxScrollPct;

    state.currentPath = pathname;
    state.pageEnteredAt = now();
    state.pageMaxScrollPct = 0;
    scrollTarget = null;

    if (previousPath === null) return;
    trackEvent(EVENTS.ROUTE_VIEW, {
      meta: {
        path: pathname,
        prevPath: previousPath,
        prevSec: previousSec,
        prevScrollPct: previousScroll,
        index: clock.pages,
      },
    });
  } catch (error) {
    // ignore
  }
}
